import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Tag, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `/api`;

const ProductsPage = () => {
  const [groups, setGroups] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [groupsRes, offersRes] = await Promise.all([
          axios.get(`${API}/groups`),
          axios.get(`${API}/offers?active_only=true`)
        ]);
        setGroups(groupsRes.data);
        setOffers(offersRes.data);
      } catch {
        // Error fetching data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="products-page">
      <Helmet>
        <title>Our Fabric Collections | DUGARS</title>
        <meta name="description" content="Browse DUGARS fabric collections — nylon rayon plain dyed and structural knits. Each collection features multiple colour options with stock details." />
        <link rel="canonical" href="https://thedugars.in/products" />
        <meta property="og:title" content="Our Fabric Collections | DUGARS" />
        <meta property="og:description" content="Browse our premium fabric collections with multiple colour options." />
        <meta property="og:url" content="https://thedugars.in/products" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Our Fabric Collections | DUGARS" />
        <meta name="twitter:description" content="Browse our premium fabric collections with multiple colour options." />
      </Helmet>
      {/* Hero */}
      <section className="bg-[#1C1C1A] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
            Our Collections
          </span>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#F7F5F0]">
            Our Collections
          </h1>
          <p className="mt-4 text-[#F7F5F0]/60 max-w-xl mx-auto text-sm">
            Browse our fabric collections. Each group contains multiple colour options with individual stock and lot details.
          </p>
        </div>
      </section>

      {/* Product Groups Grid */}
      <section className="py-16 md:py-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-[#E6E4DD]" />
                  <div className="mt-4 h-6 bg-[#E6E4DD] w-3/4" />
                  <div className="mt-2 h-4 bg-[#E6E4DD] w-1/2" />
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-20">
              <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#1C1C1A] mb-4">No Products Yet</h2>
              <p className="text-[#6B6A65]">Check back soon for our latest collections.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {groups.map((group) => (
                <Link
                  key={group.id}
                  to={`/products/${group.id}`}
                  className="group block"
                  data-testid={`group-card-${group.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden mb-4 bg-[#1C1C1A]">
                    {group.image_url ? (
                      <img
                        src={group.image_url}
                        alt={group.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center bg-gradient-to-br from-[#1C1C1A] to-[#4A5D4E] ${group.image_url ? 'hidden' : 'flex'}`}>
                      <span className="font-['Cormorant_Garamond'] text-3xl text-[#F7F5F0]/30 text-center px-6">
                        {group.name}
                      </span>
                    </div>
                    {group.variant_count > 0 && (
                      <span className="absolute top-3 right-3 px-3 py-1 text-xs font-semibold bg-white/90 text-[#1C1C1A]">
                        {group.variant_count} Colours
                      </span>
                    )}
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1C1C1A] group-hover:text-[#4A5D4E] transition-colors">
                    {group.name}
                  </h3>
                  {group.description && (
                    <p className="mt-1 text-sm text-[#6B6A65] line-clamp-2">{group.description}</p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium uppercase tracking-wider text-[#4A5D4E] group-hover:gap-2 transition-all">
                    View Colours <ChevronRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Offers Section */}
      {offers.length > 0 && (
        <section className="pb-16 md:pb-24" data-testid="offers-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="border-t border-[#E6E4DD] pt-16">
              <div className="text-center mb-12">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-3 block">
                  Special Deals
                </span>
                <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light tracking-tighter text-[#1C1C1A]">
                  Current Offers
                </h2>
              </div>
              <div className={`grid gap-6 ${offers.length === 1 ? 'max-w-lg mx-auto' : offers.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                {offers.map((offer) => (
                  <div
                    key={offer.id}
                    className="group relative bg-[#1C1C1A] p-8 flex flex-col justify-between overflow-hidden"
                    data-testid={`offer-card-${offer.id}`}
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-[#4A5D4E]/20 -translate-y-6 translate-x-6 rotate-45" />
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Tag size={14} className="text-[#4A5D4E]" />
                        <span className="text-xs uppercase tracking-widest text-[#4A5D4E] font-semibold">
                          {offer.discount_text}
                        </span>
                      </div>
                      <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#F7F5F0] mb-3">
                        {offer.title}
                      </h3>
                      <p className="text-[#F7F5F0]/60 text-sm leading-relaxed">
                        {offer.description}
                      </p>
                    </div>
                    {offer.valid_until && (
                      <p className="text-[#F7F5F0]/40 text-xs mt-6 pt-4 border-t border-[#F7F5F0]/10">
                        Valid until {offer.valid_until}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductsPage;
